'use client';
import * as Sentry from '@sentry/nextjs';
import { Component, ErrorInfo, ReactNode } from 'react';

type FallbackRenderProps = {
    error: Error;
    reset: () => void;
};

type Props = {
    children: ReactNode;
    /** Render-prop fallback shown when a child render throws. */
    fallback: (props: FallbackRenderProps) => ReactNode;
    /**
     * Extra Sentry tags attached to the captured exception (e.g. { area: 'cart' }).
     * They also feed the fingerprint so similar errors group together instead of
     * flooding Sentry on every request.
     */
    sentryTags?: Record<string, string>;
    /**
     * Called right before the boundary resets its state. Use it to clear the
     * corrupt state that caused the crash (e.g. remove a broken cart from
     * localStorage) so the retry does not immediately fail again.
     */
    onReset?: () => void;
    /** Disable Sentry reporting (e.g. in the editor where the admin sees the error live). */
    skipSentry?: boolean;
};

type State = {
    error: Error | null;
};

export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (this.props.skipSentry) return;

        Sentry.withScope((scope) => {
            const tags = this.props.sentryTags;
            if (tags) {
                Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
                // Deduplicate by area so a config/data error that re-renders on every
                // request does not create a new Sentry issue each time.
                scope.setFingerprint(['error-boundary', ...Object.values(tags)]);
            }
            scope.setContext('react', { componentStack: errorInfo.componentStack });
            Sentry.captureException(error);
        });
    }

    reset = () => {
        this.props.onReset?.();
        this.setState({ error: null });
    };

    render() {
        if (this.state.error) {
            return this.props.fallback({ error: this.state.error, reset: this.reset });
        }
        return this.props.children;
    }
}
